import { useEffect, useRef } from 'react';
import _ from 'lodash';
import moment from 'moment';
import {
  AI_COMMAND,
  canAvgRhythmEvents,
  canShowCountBeatEvents,
  canShowDurationEvents,
  ECG_EVENT_MENU_OPTIONS,
  EVENT_COUNT_CONVERT,
  KEY_CANCEL,
  KEY_RECOIL,
  LIST_RUN_EVENT,
  STRIP_EVENT_THUMBNAIL_INFO,
  TYPE_RHYTHM_EVENT_ENUM,
  EMITTER_CONSTANTS,
} from '@/shared/config';
import { useEmitter } from '@/shared/lib/customHooks';
import { generateDefaultHeatmapState } from '@/shared/lib/heatmap';
import { removeSavingData } from '@/entities/beat';
import {
  selectActiveTab,
  selectEventFilter,
  selectIsActiveTab,
  selectPageIndex,
  selectSelectedDateValue,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';
import { logError } from '@/shared/lib/apiHelpers';
import { clearApolloCache } from '@/shared/api';
import {
  generateFilterSpecialEventType,
  isAppliedFilterEvent,
  fetchHolterEventIndex,
  fetchHolterEvents,
  fetchHolterEventsDailyCount,
  fetchHolterRhythmEventsCount,
} from '@/entities/event';
import emitter from '@/shared/lib/eventEmitter';

// Apollo Functions
import { fetchHolterBeatEventsCount } from '@/entities/beat';
import { DATA_HEATMAP_STATUS } from '@/shared/model';
import type { IEvent, IReportInfo } from '@/shared/model';

type EventsDataEffectProps = { keyRecoil: string };

/**
 * Keeps the ECG events tab's store data in step with the AI processing updates arriving over the socket emitter. Fetches the beat and rhythm counts on mount, then for each event, re-capture or minutely-HR message either queues it while a save is in flight or reloads the daily counts, page and selection at once, flushing that queue and dropping the pending local changes when saving stops.
 *
 * @param keyRecoil - Store key of the tab whose event data this effect maintains.
 * @returns Null, since the component renders no markup.
 */
const EventsDataEffect = ({ keyRecoil }: EventsDataEffectProps): null => {
  const { studyId, profileId, startReport, stopReport } = useReportStore(
    (s) => s.reportInfo,
  ) as IReportInfo;

  // use recoil getter
  /**
   * Reads which ECG event type the tab is currently showing. Applies the tab's active-tab selector to the report store's current snapshot, so socket handlers see the live value without subscribing.
   *
   * @returns The active rhythm, beat or beat-HR event type.
   */
  const getActiveButton = () => selectActiveTab(keyRecoil)(useReportStore.getState());
  /**
   * Reads the event, beat or bookmark strip currently selected on the tab. Applies the tab's selected-strip selector to the report store's snapshot.
   *
   * @returns The currently selected strip.
   */
  const getSelectedStrip = () => selectSelectedStrip(keyRecoil)(useReportStore.getState());
  /**
   * Reads which page of event strips the tab is showing. Applies the tab's page-index selector to the report store's current snapshot.
   *
   * @returns The current page index.
   */
  const getPageIndex = () => selectPageIndex(keyRecoil)(useReportStore.getState());
  /**
   * Reads the event filter applied on this tab. Applies the tab's filter selector to the report store's snapshot, so reload handlers pick up filter changes without re-subscribing.
   *
   * @returns The tab's current event filter.
   */
  const getEventFilter = () => selectEventFilter(keyRecoil)(useReportStore.getState());
  /**
   * Reads the day selected on the rhythm-event tab. Applies the tab-1 selector to the report store's snapshot, since the reload handlers need that day even while serving another tab.
   *
   * @returns The ISO date string of the selected day.
   */
  const getSelectedDateValue = () =>
    selectSelectedDateValue(KEY_RECOIL.BEAT_HR)(useReportStore.getState());
  const setArtifactDailyCountAction = useReportStore((s) => s.actions.setDailyArtifactCount);
  /**
   * Stores the per-day artifact event counts shown on the rhythm-event tab. Forwards the value to the store action with the tab-1 key bound, whichever tab this effect itself serves.
   *
   * @param v - The new artifact daily-count array, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setArtifactDailyCount = (v: Parameters<typeof setArtifactDailyCountAction>[1]) =>
    setArtifactDailyCountAction(KEY_RECOIL.BEAT_HR, v);

  // use recoil setter
  const setReloadEcgViewerAction = useReportStore((s) => s.actions.setReloadEcgViewer);
  /**
   * Bumps the key that forces the ECG viewer to reload. Forwards the value to the store action with the tab key already bound; the handlers add a random increment so the value always changes.
   *
   * @param v - The new reload counter, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setReloadEcgViewer = (v: Parameters<typeof setReloadEcgViewerAction>[1]) =>
    setReloadEcgViewerAction(keyRecoil, v);
  const setBeatChangesAction = useReportStore((s) => s.actions.setBeatChanges);
  /**
   * Stores the tab's pending beat edits. Forwards the value to the store action with the tab key already bound, so callers pass only the new list.
   *
   * @param v - The new beat-change list, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setBeatChanges = (v: Parameters<typeof setBeatChangesAction>[1]) =>
    setBeatChangesAction(keyRecoil, v);
  const setEventChangesAction = useReportStore((s) => s.actions.setEventChanges);
  /**
   * Stores the tab's edited events that are still awaiting a save. Forwards the value to the store action with the tab key already bound.
   *
   * @param v - The new changed-event list, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setEventChanges = (v: Parameters<typeof setEventChangesAction>[1]) =>
    setEventChangesAction(keyRecoil, v);
  const setBeatOptionsAction = useReportStore((s) => s.actions.setBeatOptions);
  /**
   * Stores the beat option bundle that mirrors the pending beat changes for consumers. Forwards the value to the store action with the tab key already bound.
   *
   * @param v - The new beat options, or an updater receiving the previous ones.
   * @returns Nothing.
   */
  const setBeatOptions = (v: Parameters<typeof setBeatOptionsAction>[1]) =>
    setBeatOptionsAction(keyRecoil, v);
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Stores the event, beat or bookmark strip selected on the tab. Forwards the value to the store action with the tab key already bound; passing null clears the selection.
   *
   * @param v - The newly selected strip, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);
  const setReloadHrChartAction = useReportStore((s) => s.actions.setReloadHrChart);
  /**
   * Bumps the key that forces the HR chart to reload. Forwards the value to the store action with the tab key already bound; the handlers add a random increment so the value always changes.
   *
   * @param v - The new reload counter, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setReloadHrChart = (v: Parameters<typeof setReloadHrChartAction>[1]) =>
    setReloadHrChartAction(keyRecoil, v);
  const setEventOthersAction = useReportStore((s) => s.actions.setEventOthers);
  /**
   * Stores the tab's remaining pending event edits, the ones outside the new and changed lists. Forwards the value to the store action with the tab key already bound.
   *
   * @param v - The new other-event list, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setEventOthers = (v: Parameters<typeof setEventOthersAction>[1]) =>
    setEventOthersAction(keyRecoil, v);
  const setEventNewsAction = useReportStore((s) => s.actions.setEventNews);
  /**
   * Stores the tab's newly created events that are still awaiting a save. Forwards the value to the store action with the tab key already bound.
   *
   * @param v - The new list of new events, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setEventNews = (v: Parameters<typeof setEventNewsAction>[1]) =>
    setEventNewsAction(keyRecoil, v);
  const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
  /**
   * Stores the event option bundle mirroring the new, changed and other event lists. Forwards the value to the store action with the tab key already bound.
   *
   * @param v - The new event options, or an updater receiving the previous ones.
   * @returns Nothing.
   */
  const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
    setEventOptionsAction(keyRecoil, v);
  const setDailyCountAction = useReportStore((s) => s.actions.setDailyCount);
  /**
   * Stores the per-day event counts for the tab's active event type. Forwards the value to the store action with the tab key already bound.
   *
   * @param v - The new daily-count array, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setDailyCount = (v: Parameters<typeof setDailyCountAction>[1]) =>
    setDailyCountAction(keyRecoil, v);
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Stores which page of event strips the tab shows. Forwards the value to the store action with the tab key already bound.
   *
   * @param v - The new page index, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  const setGroupEventChangesAction = useReportStore((s) => s.actions.setGroupEventChanges);
  /**
   * Stores the grouped view of the pending event changes. Forwards the value to the store action with the tab key already bound.
   *
   * @param v - The new grouped-change list, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setGroupEventChanges = (v: Parameters<typeof setGroupEventChangesAction>[1]) =>
    setGroupEventChangesAction(keyRecoil, v);
  const setDataHeatmapChartStatus = useReportStore((s) => s.actions.setDataHeatmapChartStatus);
  const setListSelectedCells = useReportStore((s) => s.actions.setListSelectedCells);

  // use recoil value
  const isActiveTab = useReportStore(selectIsActiveTab(keyRecoil));

  // use recoil getter not keyRecoil
  /**
   * Reads the ECG data map currently loaded for the study. Takes it off the report store's snapshot so socket handlers can clone and patch it without subscribing.
   *
   * @returns The current ECG data map.
   */
  const getEcgDataMap = () => useReportStore.getState().ecgDataMap;
  /**
   * Reads the study's special event type configuration. Takes specialEventTypes off the report store's current snapshot.
   *
   * @returns The study's special event types.
   */
  const getSpecialEventTypes = () => useReportStore.getState().specialEventTypes;
  /**
   * Reads the cached per-type counts of Holter beat events. Takes beatEventCount off the report store's snapshot, which serves as the fallback when a fetch brings back no fresh count.
   *
   * @returns The current Holter beat event counts.
   */
  const getHolterBeatEventsCount = () => useReportStore.getState().beatEventCount;
  /**
   * Reads the cached per-type counts of Holter rhythm events. Takes rhythmEventCount off the report store's snapshot, which serves as the fallback when a fetch brings back no fresh count.
   *
   * @returns The current Holter rhythm event counts.
   */
  const getHolterRhythmEventsCount = () => useReportStore.getState().rhythmEventCount;

  // use recoil setter not keyRecoil
  const setTotalStrip = useReportStore((s) => s.actions.setTotalStrip);
  const setEcgDataMap = useReportStore((s) => s.actions.setEcgDataMap);

  const setHolterRhythmEventsCount = useReportStore((s) => s.actions.setRhythmEventCount);
  const setHolterBeatEventsCount = useReportStore((s) => s.actions.setBeatEventCount);

  // use recoil state
  const isPushQueue = useReportStore((s) => s.isPushQueue);
  const setIsPushQueue = useReportStore((s) => s.actions.setIsPushQueue);

  const updateDataMessageQueue = useRef([]);

  /**
   * Refreshes the study's Holter beat and rhythm event counts. Issues both count queries in parallel for the study and profile, writes each result into the report store, and logs a failure without rethrowing.
   *
   * @returns A promise resolving once both event counts are stored.
   */
  const fetchEventsCount = async () => {
    try {
      const eventsCountFilter = {
        studyId,
        profileId,
      };
      const promises = [
        fetchHolterBeatEventsCount(eventsCountFilter, true, null, false),
        fetchHolterRhythmEventsCount(eventsCountFilter, true, null, false),
      ];
      const [holterBeatEventsCount, holterRhythmEventsCount] = await Promise.all(promises);
      setHolterBeatEventsCount(holterBeatEventsCount);
      setHolterRhythmEventsCount(holterRhythmEventsCount);
    } catch (error) {
      logError('Failed to fetch holter rhythm events count: ', error);
    }
  };

  /**
   * Fetches one page of Holter event strips for the tab's active event type. Clamps a negative page to zero, derives the skip from the strip display limit, and when an extra filter is given also fetches a matching count from the beat or rhythm endpoint chosen by the type's menu entry, falling back to the cached counts and, on error, to undefined events.
   *
   * @param page - Zero-based page of strips to fetch.
   * @param additionalFilter - Extra query filter; when non-empty it also triggers a fresh event count.
   * @returns A promise resolving to an object carrying the fetched events and their count.
   */
  const handleFetchStripEvents = async ({
    page,
    additionalFilter = {},
  }: {
    page: number;
    additionalFilter?: any;
  }) => {
    try {
      const type = getActiveButton();
      const validatePage = page <= 0 ? 0 : page;
      const filterHolterEvents = {
        studyId,
        profileId,
        types: [type],
        skip: validatePage * STRIP_EVENT_THUMBNAIL_INFO.stripDisplayLimit,
        ...additionalFilter,
      };
      const promises: Promise<any>[] = [
        fetchHolterEvents(
          filterHolterEvents,
          STRIP_EVENT_THUMBNAIL_INFO.stripDisplayLimit,
          true,
          KEY_CANCEL.API_HOLTER_AI,
        ),
      ];
      if (!_.isEmpty(additionalFilter)) {
        const holterEventsCountFilter = {
          studyId,
          profileId,
          types: [type],
          ...additionalFilter,
        };
        promises.push(
          _.find(ECG_EVENT_MENU_OPTIONS, (x) => x.value === type).type === 'beat'
            ? fetchHolterBeatEventsCount(holterEventsCountFilter, true, KEY_CANCEL.API_HOLTER_AI)
            : fetchHolterRhythmEventsCount(holterEventsCountFilter, true, KEY_CANCEL.API_HOLTER_AI),
        );
      }
      const [{ events }, eventsCount] = (await Promise.all(promises)) as [any, any];
      if (!_.isEmpty(additionalFilter)) {
        return { events, eventsCount };
      }
      return {
        events,
        eventsCount:
          _.find(ECG_EVENT_MENU_OPTIONS, (x) => x.value === type).type === 'beat'
            ? getHolterBeatEventsCount()
            : getHolterRhythmEventsCount(),
      };
    } catch (error) {
      return { events: undefined, eventsCount: 0 };
    }
  };

  /**
   * Reloads the event tab after the AI has updated events for the study. Refetches the per-day counts for the active type, re-locates the selected event under the current filter so it stays on screen, refreshes the artifact daily count and total strip count, and bumps the HR chart and ECG viewer reload keys for a re-capture or an AFib artifact change.
   *
   * @param msg - Socket payload describing the update, including isAFibArtifact, isRecapture and recaptureEventsAfterUpdating.
   * @param isOutSideCommand - True when the update arrived outside the current save flow, which enables the Apollo cache clear and forced re-render.
   * @returns A promise resolving once the tab's counts, selection and reload keys are updated.
   */
  const handleReloadEvent = async (msg: any, isOutSideCommand = false) => {
    try {
      const { isAFibArtifact } = msg;
      const selectedDateValue = getSelectedDateValue();
      const type = getActiveButton();

      // Tạo filter cho dailyCount
      const filterDailyCount = {
        studyId,
        profileId,
        types: [type],
        startSearchDate: startReport,
        stopSearchDate: stopReport,
      };

      // Fetch dữ liệu dailyCount
      const dailyCount = await fetchHolterEventsDailyCount(filterDailyCount);
      if (!_.isEmpty(dailyCount)) {
        setDailyCount(dailyCount);
      }

      const selectedStrip = getSelectedStrip();
      // selectedStripState is IEvent | IBeat | IEcgBookmark | null; idEvent only exists on IEvent.
      if ((selectedStrip as IEvent)?.idEvent) {
        let filterHolterEventIndex: any = {};
        if (type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT) {
          filterHolterEventIndex = {
            studyId,
            profileId,
            types: [type],
            includedEventId: (selectedStrip as IEvent).idEvent,
            startRange: {
              start: selectedDateValue,
              stop: moment(selectedDateValue).add(1, 'days').toISOString(),
            },
          };
        } else {
          const additionalFilter = {};
          const eventFilter = getEventFilter();
          if (isAppliedFilterEvent(eventFilter, type)) {
            _.assign(additionalFilter, {
              ...(eventFilter.isHideReviewed && { isReviewed: false }),
              ...(eventFilter.isCapture && { isCaptured: true }),
              ...(eventFilter.isAvgHrBelow100 &&
                LIST_RUN_EVENT.includes(type as any) && { isAvgHrBelow100: true }),
              ...(eventFilter.sortOrder &&
                (canShowCountBeatEvents.includes(type as any) ||
                  canShowDurationEvents.includes(type as any) ||
                  canAvgRhythmEvents.includes(type as any)) && {
                  sortBy: eventFilter.sortBy || 'countBeats',
                  sortOrder: eventFilter.sortOrder,
                }),
            });
          }

          filterHolterEventIndex = {
            studyId,
            profileId,
            types: [type],
            includedEventId: (selectedStrip as IEvent).idEvent,
            ...additionalFilter,
          };
        }

        const eventIndex = await fetchHolterEventIndex(
          filterHolterEventIndex,
          false,
          KEY_CANCEL.API_HOLTER_AI,
        );
        if (!_.isNil(eventIndex) && eventIndex >= 0) {
          const page = Math.floor(eventIndex / STRIP_EVENT_THUMBNAIL_INFO.stripDisplayLimit);
          setPageIndex({ index: page < 0 ? 0 : page });
        } else {
          const pageIndex = getPageIndex();
          const additionalFilter = generateFilterSpecialEventType(type, getSpecialEventTypes());
          const { events, eventsCount } = await handleFetchStripEvents({
            page: pageIndex.index,
            additionalFilter,
          });
          if (_.isEmpty(events)) {
            const totalEvent =
              eventsCount[(EVENT_COUNT_CONVERT as any)[getActiveButton()]]?.count || 0;
            const pageTemp = Math.floor(totalEvent / STRIP_EVENT_THUMBNAIL_INFO.stripDisplayLimit);
            const page =
              totalEvent % STRIP_EVENT_THUMBNAIL_INFO.stripDisplayLimit === 0
                ? pageTemp - 1
                : pageTemp;
            setPageIndex({ index: page < 0 ? 0 : page });
          } else {
            setPageIndex({ index: getPageIndex().index });
          }
        }
      }

      const filterDailyArtifactCount = {
        studyId,
        profileId,
        types: [TYPE_RHYTHM_EVENT_ENUM.ARTIFACT],
      };
      const dailyArtifactCount = await fetchHolterEventsDailyCount(filterDailyArtifactCount);
      const currentSelectedDateValue = getSelectedDateValue();
      const foundDailyCount = _.find(
        dailyArtifactCount,
        (x) => x.date === currentSelectedDateValue,
      )?.count;
      setTotalStrip(foundDailyCount ?? 0);
      setArtifactDailyCount(dailyArtifactCount);

      if (isOutSideCommand && msg.isRecapture && msg.recaptureEventsAfterUpdating && isActiveTab) {
        await clearApolloCache();
        setReloadHrChart((prev) => prev + _.round(Math.random() * 100));
        setReloadEcgViewer((prev) => prev + _.round(Math.random() * 100));
        emitter.emit(EMITTER_CONSTANTS.RE_RENDER_ECG_VIEWER);
      } else if (isAFibArtifact) {
        setReloadHrChart((prev) => prev + _.round(Math.random() * 100));
        setReloadEcgViewer((prev) => prev + _.round(Math.random() * 100));
      }
    } catch (error) {
      console.error('Error handling reload event:', error);
    }
  };

  /**
   * Applies minutely heart-rate summaries pushed from the server onto the loaded ECG data. Copies the data map, patches the average, maximum and minimum HR of each summary onto the matching entry, stores the clone only when it actually differs, then asks the HR chart to re-render.
   *
   * @param msg - Socket payload carrying the summaries, either directly or under beatsUpdated.
   * @returns A promise resolving once the patched data map is stored.
   */
  const handleMinutlySummary = async (msg: any) => {
    const prevEcgDataMap = getEcgDataMap();
    const cloneEcgDataMap = { ...prevEcgDataMap };
    if (
      (msg.summaries?.length || msg.beatsUpdated?.summaries?.length) &&
      !_.isEmpty(prevEcgDataMap?.data)
    ) {
      _.forEach(msg.summaries || msg.beatsUpdated?.summaries, (summary) => {
        const foundData = _.find(cloneEcgDataMap.data, (x) => x.id === summary.id);
        if (foundData) {
          _.assign(foundData, {
            avgHrs: summary?.avgHrs,
            maxHrs: summary?.maxHrs,
            minHrs: summary?.minHrs,
          });
        }
      });
    }
    setEcgDataMap((prev: any) => {
      if (_.isEqual(prev, cloneEcgDataMap)) {
        return prev;
      }
      return cloneEcgDataMap;
    });
    emitter.emit(EMITTER_CONSTANTS.RE_RENDER_HR_CHART);
  };

  /**
   * Drops the locally pending beat and event edits once the server has persisted them. Strips the saving-marked entries out of the beat changes and out of the new, changed and other event lists, mirrors the survivors into the beat and event option bundles, resets the heatmap status and cell selection, and clears the selection when the selected strip was a new event.
   *
   * @returns Nothing.
   */
  const removeChangeData = () => {
    let beatsChanges: any[] = [];

    setBeatChanges((prev: any) => {
      if (_.isEmpty(prev)) return prev;
      const beats = removeSavingData(prev);
      beatsChanges = beats;
      return beats;
    });
    setBeatOptions((prev: any) => ({ ...prev, beatChangesState: beatsChanges }));

    let eventChanges: any[] = [];
    let eventNews: any[] = [];
    let eventOthers: any[] = [];
    setGroupEventChanges((prev: any) => {
      if (prev.length === 0) {
        return prev;
      }
      return [];
    });
    setEventChanges((prev: any) => {
      let count = 0;
      const filterResult = prev.filter((x: any) => {
        if (x.isSaving) {
          count += 1;
          return false;
        }
        return true;
      });
      if (count === 0) {
        return prev;
      }
      eventChanges.push(...filterResult);
      return filterResult;
    });
    setEventOthers((prev: any) => {
      let count = 0;
      const filterResult = prev.filter((x: any) => {
        if (x.isSaving) {
          count += 1;
          return false;
        }
        return true;
      });
      if (count === 0) {
        return prev;
      }
      eventOthers.push(...filterResult);
      return filterResult;
    });
    setEventNews((prev: any) => {
      let count = 0;
      const filterResult = prev.filter((x: any) => {
        if (x.isSaving) {
          count += 1;
          return false;
        }
        return true;
      });
      if (count === 0) {
        return prev;
      }
      eventNews.push(...filterResult);
      return filterResult;
    });

    setDataHeatmapChartStatus(generateDefaultHeatmapState(DATA_HEATMAP_STATUS.INITIAL));
    setListSelectedCells([]);

    setEventOptions({
      eventNewsState: eventNews,
      eventChangesState: eventChanges,
      eventOthersState: eventOthers,
    });
    const selectedStrip = getSelectedStrip();
    if ((selectedStrip as IEvent)?.isNew) {
      setSelectedStrip(null);
    }
    eventChanges = [];
    eventNews = [];
    eventOthers = [];
  };

  // TODO SAVE SOCKET
  /**
   * Flushes everything queued while an AI save was running. Clears the Apollo cache, replays each queued event or minutely-summary message through its handler, drops the locally pending changes, and always empties the queue and emits the loading-off and viewer re-render signals.
   *
   * @returns A promise resolving once the queued updates have been replayed.
   */
  const handleSavingStatus = async () => {
    try {
      await clearApolloCache();
      const promises: Promise<any>[] = [];
      updateDataMessageQueue.current.forEach((x: any) => {
        if (x.type === AI_COMMAND.EVENT) {
          promises.push(fetchEventsCount());
          promises.push(handleReloadEvent(x.msg));
        }
        if (x.type === AI_COMMAND.MINUTLY_SUMMARY) {
          promises.push(handleMinutlySummary(x.msg));
        }
      });
      removeChangeData();
      await Promise.all(promises);
    } catch (error) {
      logError('Error: ', error);
    } finally {
      updateDataMessageQueue.current.length = 0;
      emitter.emit(EMITTER_CONSTANTS.AI_LOADING, { isLoading: false });
      emitter.emit(EMITTER_CONSTANTS.RE_RENDER_ECG_VIEWER);
    }
  };

  useEffect(() => {
    fetchEventsCount();
  }, []);

  // EMITTER FROM SOCKET
  useEmitter(
    EMITTER_CONSTANTS.EVENTSUPDATED_EVENT,
    async (msg) => {
      if (!isActiveTab) return;
      if (isPushQueue) {
        updateDataMessageQueue.current.push({
          type: AI_COMMAND.EVENT,
          msg: _.cloneDeep(msg),
        });
      } else {
        await handleReloadEvent(msg, true);
        await fetchEventsCount();
      }
    },
    [isActiveTab, isPushQueue],
  );

  useEmitter(
    EMITTER_CONSTANTS.RE_CAPTURE_AUTO_EVENTS,
    (msg) => {
      if (!isActiveTab) return;
      handleReloadEvent(msg, true);
      fetchEventsCount();
    },
    [isActiveTab],
  );

  useEmitter(
    EMITTER_CONSTANTS.MINUTELY_HR_UPDATED,
    async (msg) => {
      if (!isActiveTab) return;
      if (isPushQueue) {
        updateDataMessageQueue.current.push({
          type: AI_COMMAND.MINUTLY_SUMMARY,
          msg,
        });
      } else {
        await handleMinutlySummary(msg);
      }
    },
    [isActiveTab, isPushQueue],
  );

  useEmitter(
    EMITTER_CONSTANTS.STOP_SAVING,
    async (msg: any) => {
      if (msg && isActiveTab) {
        if (msg?.data === 'done') {
          setIsPushQueue(false);
          console.time('Execution-time-saving');
          await handleSavingStatus();
          console.timeEnd('Execution-time-saving');
        }
      }
    },
    [isActiveTab],
  );

  return null;
};

export default EventsDataEffect;
